/**
 * annexesApi — RTK Query slice cho /purchase/annexes/*.
 * Tag enhanced: 'Annex'. Đồng thời invalidate 'LocalP' / 'ContractAll' nếu cần.
 *
 * Endpoints (Sprint 5):
 *   GET    /purchase/annexes/list?contract_no=&annex_date_from=&annex_date_to=&page=&page_size=
 *   GET    /purchase/annexes/suggest-no?contract_no=
 *   POST   /purchase/annexes/create
 *   GET    /purchase/annexes/{id}
 *   PUT    /purchase/annexes/{id}
 *   DELETE /purchase/annexes/{id}
 *   GET    /purchase/annexes/{id}/print[?cancel=1]
 */
import { baseApi } from './baseApi'
import type {
  AnnexCreateIn,
  AnnexListQuery,
  AnnexOut,
  AnnexUpdateIn,
  DataResponse,
  LocalPPrintResult,
  MessageResponse,
  PageResponse,
} from '@/types/api'

const annexEnhanced = baseApi.enhanceEndpoints({ addTagTypes: ['Annex'] })

export const annexesApi = annexEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listAnnexes: b.query<PageResponse<AnnexOut>, AnnexListQuery | void>({
      query: (params) => ({ url: '/purchase/annexes/list', params: params ?? {} }),
      providesTags: (res, _e, arg) => {
        const contractNo = (arg && (arg as AnnexListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((it) => ({ type: 'Annex' as const, id: it.id })),
              { type: 'Annex' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'Annex' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'Annex' as const, id: 'LIST' }]
      },
    }),
    getAnnex: b.query<DataResponse<AnnexOut>, number>({
      query: (id) => `/purchase/annexes/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Annex', id }],
    }),
    suggestAnnexNo: b.query<{ suggestedNo: string }, { contract_no: string }>({
      query: ({ contract_no }) => ({
        url: '/purchase/annexes/suggest-no',
        params: { contract_no },
      }),
      transformResponse: (r: { suggestedNo?: string; suggested_no?: string }) => ({
        suggestedNo: r.suggestedNo ?? r.suggested_no ?? '',
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'Annex', id: `SUGGEST-${arg.contract_no}` },
      ],
    }),
    /** Pre-save check (skill access-suggest) — composite (contract_no, annex_no). */
    checkAnnexNoExists: b.query<
      { exists: boolean },
      { contractNo: string; annexNo: string }
    >({
      query: ({ contractNo, annexNo }) => ({
        url: '/purchase/annexes/check-no',
        params: { contractNo, annexNo },
      }),
      keepUnusedDataFor: 0,
    }),
    createAnnex: b.mutation<DataResponse<AnnexOut>, AnnexCreateIn>({
      query: (body) => ({
        url: '/purchase/annexes/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Annex', id: 'LIST' }],
    }),
    updateAnnex: b.mutation<
      DataResponse<AnnexOut>,
      { id: number; body: AnnexUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/annexes/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Annex', id: arg.id },
        { type: 'Annex', id: 'LIST' },
      ],
    }),
    deleteAnnex: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/annexes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Annex', id },
        { type: 'Annex', id: 'LIST' },
      ],
    }),
    /** Print Amendment (HTML preview flow — Access btnPrint_Click →
     *  `DoCmd.OpenReport "rpPurchaseAmendment"`). Response shape:
     *  `{ data: { pdf_base64: '', filename: 'AX-…-amendment.pdf', html: '…' }, message }`.
     *
     *  Chấp nhận cả `number` (signature mới) và `{ id, cancel? }` (legacy) để
     *  chịu được callers cũ / stale bundle. URL bỏ trailing `?` khi không có
     *  cancel param. Defensive: throw nếu id không phải int dương để tránh
     *  URL `/purchase/annexes/undefined/print`. */
    printAnnex: b.query<
      DataResponse<LocalPPrintResult>,
      number | { id: number; cancel?: boolean }
    >({
      query: (arg) => {
        const id = typeof arg === 'number' ? arg : arg?.id
        if (!Number.isInteger(id) || (id as number) <= 0) {
          throw new Error(`Invalid annex id: ${String(id)}`)
        }
        const cancel = typeof arg === 'object' && arg ? arg.cancel : undefined
        return {
          url: `/purchase/annexes/${id}/print`,
          ...(cancel ? { params: { cancel: 1 } } : {}),
        }
      },
    }),
  }),
})

export const {
  useListAnnexesQuery,
  useGetAnnexQuery,
  useSuggestAnnexNoQuery,
  useLazySuggestAnnexNoQuery,
  useLazyCheckAnnexNoExistsQuery,
  useCreateAnnexMutation,
  useUpdateAnnexMutation,
  useDeleteAnnexMutation,
  useLazyPrintAnnexQuery,
} = annexesApi
